/** common */
import React, { useEffect, useRef, useState } from "react";
import { CSSTransition } from "react-transition-group";

/** store */
import useChatbotAPIStore from "apis/chatbot/chatbot.api";
import useChatbotStore from "store/chatbot/useChatbotStore";

/** hook */
import { useLocation } from "react-router-dom";

/** style */
import { StyledChatbotPopup } from "./style";

/** component */
import { Button } from "components/button/Button/Button";
import AutoHeightTextArea from "components/textarea/AutoHeightTextArea/AutoHeightTextArea";
import UserBubble from "./bubbleItem/UserBubble";
import AssistantBubble from "./bubbleItem/AssistantBubble";
import { StyledBubbleItem } from "./bubbleItem/style";
import { Icon } from "components/icon/Icon";
import { marked } from "marked";

type ChatItemType = {
  type: "user" | "assistant";
  content: string;
  status?: "success" | "error";
};

const HANDLE_LIST = [
  "top-handle",
  "left-handle",
  "right-handle",
  "bottom-handle",
  "top-left-handle",
  "top-right-handle",
  "bottom-left-handle",
  "bottom-right-handle",
];

const CURSOR_OPTIONS: { [key in (typeof HANDLE_LIST)[number]]: string } = {
  "top-handle": "ew-resize",
  "left-handle": "ns-resize",
  "right-handle": "ew-resize",
  "bottom-handle": "ns-resize",
  "top-left-handle": "nwse-resize",
  "top-right-handle": "nesw-resize",
  "bottom-left-handle": "nesw-resize",
  "bottom-right-handle": "nwse-resize",
};

const ChatbotPopup = () => {
  /** api */
  const { getChatbotResponse } = useChatbotAPIStore();

  /** hook */
  const location = useLocation();

  /** store */
  const { isChatbotOpen, setIsChatbotOpen } = useChatbotStore();

  /** state */
  // AI와 주고 받은 질문 답변 리스트
  const [chatList, setChatList] = useState<ChatItemType[]>([
    {
      type: "assistant",
      content:
        "안녕하세요! SyncBox의 AI 어시스턴트입니다. 어떤 도움이 필요하신가요?",
    },
  ]);
  // AI 응답 생성 중 여부
  const [isLoading, setIsLoading] = useState(false);
  // 사용자 입력 메시지
  const [message, setMessage] = useState("");

  const [isInit, setIsInit] = useState(true);

  /** ref */
  // 챗봇 팝업 위치
  const popupPosition = useRef({
    x: 0,
    y: 0,
    width: 400,
    height: 500,
  });
  // 챗봇에 질문 후 가장 하단으로 스크롤을 이동시키기 위해 배치하는 요소
  const bottomRef = useRef<HTMLDivElement>(null);
  // 챗봇 팝업 요소
  const popupRef = useRef<HTMLDivElement>(null);
  // 챗봇 응답 요청 취소 컨트롤러
  const abortControllerRef = useRef<AbortController | null>(null);

  /** function */
  /**
   * @description 챗봇 응답 요청 취소 컨트롤러 초기화
   */
  const abortRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
    }
  };

  /** event handler */
  /**
   * @description 챗봇 팝업을 드래그하여 이동시키기 위한 이벤트
   * @param e
   */
  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();

    // 대상의 현재 x, y 좌표와 마우스 커서의 x, y 좌표의 차이
    const offset = {
      x: e.clientX - popupPosition.current.x,
      y: e.clientY - popupPosition.current.y,
    };

    const onMouseMove = (e: MouseEvent) => {
      const popupWidth = popupRef.current?.offsetWidth ?? 1;
      const popupHeight = popupRef.current?.offsetHeight ?? 1;

      // 화면 바깥으로 나갈 수 없도록 제한
      popupPosition.current = {
        ...popupPosition.current,
        x: Math.max(
          Math.min(e.clientX - offset.x, window.innerWidth - popupWidth / 2),
          popupWidth / 2
        ),
        y: Math.max(
          Math.min(e.clientY - offset.y, window.innerHeight - popupHeight / 2),
          popupHeight / 2
        ),
      };

      // 스타일 업데이트
      if (popupRef.current) {
        popupRef.current.style.left = `${popupPosition.current.x}px`;
        popupRef.current.style.top = `${popupPosition.current.y}px`;
      }
    };

    // 마우스 이벤트 제거
    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  /**
   * @description 챗봇에 질문 후 응답 생성
   */
  const onSendMessage = async () => {
    if (!message || isLoading) return;

    // 사용자 입력 메시지를 줄바꿈 처리 하기 위해 <br> 태그로 변환
    const tempMessage = message.replace(/(\r\n|\n|\r)/gm, "<br>");

    // 사용자 입력 메시지 초기화
    setMessage("");

    const tempChatList = [...chatList];

    try {
      setIsLoading(true);
      // 사용자 입력 메시지를 메시지 리스트에 추가
      tempChatList.push({ type: "user", content: await marked(tempMessage) });

      setChatList(tempChatList);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      const response = await getChatbotResponse(tempMessage, controller.signal);

      if (!response || !response.isSuccess) throw new Error(response.error);

      // AI 응답 메시지를 메시지 리스트에 추가
      tempChatList.push({
        type: "assistant",
        content: await marked(response.resultList),
        status: "success",
      });
      setChatList(tempChatList);
    } catch (e) {
      if (e instanceof Error && e.message === "AbortError") {
        console.error("API 호출이 취소되었습니다.");
      } else {
        tempChatList.push({
          type: "assistant",
          content: "응답 생성에 문제가 발생했습니다. 다시 시도해주세요.",
          status: "error",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * @description 챗봇 팝업 크기 조절 이벤트
   * @param e
   */
  const onHandleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();

    const resizingTarget = e.currentTarget.id;

    const startX = e.clientX;
    const startY = e.clientY;

    // 팝업 크기를 조정하는 도중 마우스가 벗어나도 커서 모양 유지
    const style = document.createElement("style");
    style.innerHTML = `
      body { 
        cursor: ${CURSOR_OPTIONS[resizingTarget]} !important; 
      }
    `;
    document.head.appendChild(style);

    const initialPosition = {
      x: popupPosition.current.x,
      y: popupPosition.current.y,
      width: popupPosition.current.width,
      height: popupPosition.current.height,
    };

    const onMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      const MIN_SIZE = 200;
      const MAX_SIZE = 800;

      const isInRange = (size: number) => size >= MIN_SIZE && size <= MAX_SIZE;

      const updatePosition = (newWidth?: number, newHeight?: number) => {
        const position = { ...popupPosition.current };

        if (newWidth && isInRange(newWidth)) {
          position.width = newWidth;
          position.x = initialPosition.x + deltaX / 2;
        }

        if (newHeight && isInRange(newHeight)) {
          position.height = newHeight;
          position.y = initialPosition.y + deltaY / 2;
        }

        popupPosition.current = position;
      };

      switch (resizingTarget) {
        case "top-handle":
          updatePosition(undefined, initialPosition.height - deltaY);
          break;

        case "left-handle":
          updatePosition(initialPosition.width - deltaX);
          break;

        case "right-handle":
          updatePosition(initialPosition.width + deltaX);
          break;

        case "bottom-handle":
          updatePosition(undefined, initialPosition.height + deltaY);
          break;

        case "top-left-handle":
          updatePosition(
            initialPosition.width - deltaX,
            initialPosition.height - deltaY
          );
          break;

        case "top-right-handle":
          updatePosition(
            initialPosition.width + deltaX,
            initialPosition.height - deltaY
          );
          break;

        case "bottom-left-handle":
          updatePosition(
            initialPosition.width - deltaX,
            initialPosition.height + deltaY
          );
          break;

        case "bottom-right-handle":
          updatePosition(
            initialPosition.width + deltaX,
            initialPosition.height + deltaY
          );
          break;
      }

      if (popupRef.current) {
        popupRef.current.style.left = `${popupPosition.current.x}px`;
        popupRef.current.style.top = `${popupPosition.current.y}px`;
        popupRef.current.style.width = `${popupPosition.current.width}px`;
        popupRef.current.style.height = `${popupPosition.current.height}px`;
      }
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.head.removeChild(style);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const onResize = () => {
    if (isChatbotOpen) {
      if (popupRef.current) {
        const popupRect = popupRef.current.getBoundingClientRect();
        const popupWidth = popupRect.width;
        const popupHeight = popupRect.height;

        if (
          popupRect.left < 0 ||
          popupRect.left > window.innerWidth ||
          popupRect.top < 0 ||
          popupRect.top > window.innerHeight
        ) {
          popupPosition.current = {
            ...popupPosition.current,
            x: popupWidth / 2 + 100,
            y: popupHeight / 2 + 100,
          };

          if (popupRef.current) {
            popupRef.current.style.left = `${popupPosition.current.x}px`;
            popupRef.current.style.top = `${popupPosition.current.y}px`;
          }
        }
      }
    }
  };

  useEffect(() => {
    if (isChatbotOpen && isInit) {
      popupPosition.current = {
        x: window.innerWidth * 0.85,
        y: window.innerHeight * 0.7,
        width: 400,
        height: 500,
      };

      if (popupRef.current) {
        popupRef.current.style.left = `${popupPosition.current.x}px`;
        popupRef.current.style.top = `${popupPosition.current.y}px`;
        popupRef.current.style.width = `${popupPosition.current.width}px`;
        popupRef.current.style.height = `${popupPosition.current.height}px`;
      }
      setIsInit(false);
      window.addEventListener("resize", onResize);
    }

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [isChatbotOpen]);

  useEffect(() => {
    setIsChatbotOpen(false);
  }, [location]);

  useEffect(() => {
    if (bottomRef.current && isChatbotOpen) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatList, isChatbotOpen]);

  return (
    <CSSTransition
      in={isChatbotOpen}
      timeout={500}
      classNames="popup-chatbot"
      mountOnEnter
    >
      <StyledChatbotPopup ref={popupRef}>
        {HANDLE_LIST.map((handle) => (
          <div
            className={handle}
            id={handle}
            key={handle}
            onMouseDown={onHandleMouseDown}
          ></div>
        ))}
        <header onMouseDown={onMouseDown}>
          <h1>
            <Icon
              icon="Stars02"
              size={16}
              stroke="--text-gray-normal"
              fill="--text-gray-normal"
            />
            AI Assistant
          </h1>
          <Button
            type="text"
            preIcon="XCloseLg"
            color="normal"
            size="small"
            onClick={() => setIsChatbotOpen(false)}
          />
        </header>
        <main>
          {isChatbotOpen &&
            chatList.map((item, index) => {
              if (item.type === "user")
                return <UserBubble key={index} content={item.content} />;
              else
                return (
                  <AssistantBubble
                    key={index}
                    content={item.content}
                    status={item.status}
                  />
                );
            })}
          {isLoading && (
            <StyledBubbleItem className="loading-message">
              <span>응답을 생성 중입니다...</span>
            </StyledBubbleItem>
          )}
          <div ref={bottomRef} style={{ height: "1px" }}></div>
        </main>
        <footer>
          <AutoHeightTextArea
            placeholder="무엇을 도와드릴까요?"
            value={message}
            onValueChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setMessage(e.target.value)
            }
            onKeyDown={async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
              }
            }}
            onKeyUp={async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                await onSendMessage();
              }
            }}
            maxHeight="10rem"
          />
          <Button
            className="ai-button"
            width="4rem"
            type="solid"
            preIcon={isLoading ? "Stop" : "Send01"}
            color="primary"
            size="large"
            disabled={!message && !isLoading}
            onClick={isLoading ? abortRequest : onSendMessage}
          />
        </footer>
      </StyledChatbotPopup>
    </CSSTransition>
  );
};

export default ChatbotPopup;
